import { useEffect, type ReactNode } from 'react';
import { useAuthStore } from './useAuthStore';
import { axiosClient, ensureFreshToken } from '@/shared/api/axiosClient';
import type { User } from '@/entities/user/model/user.types';
import type { ApiSuccess } from '@/shared/api/apiResponse.types';

type Props = { children: ReactNode };

export function AuthInitializer({ children }: Props) {
  const { _hydrated, idToken, expiresAt, refreshToken, setUser, clear } = useAuthStore();

  useEffect(() => {
    if (!_hydrated) return;
    if (!idToken && !refreshToken) return;

    const tokenStillValid = idToken && expiresAt && expiresAt - Date.now() > 5 * 60 * 1000;

    if (!tokenStillValid && !refreshToken) {
      clear();
      return;
    }

    (async () => {
      try {
        // Refreshes via the same Cognito app client that issued the tokens
        // (guest vs DJ) — a mismatch here used to log guests out on reopen
        if (!tokenStillValid) await ensureFreshToken();
        if (!useAuthStore.getState().user) {
          const res = await axiosClient.get<ApiSuccess<User>>('/me');
          setUser(res.data.data);
        }
      } catch (err) {
        // Refresh failures already clear the store inside ensureFreshToken.
        // For /me, only drop the session on a definitive auth rejection —
        // a flaky network at boot shouldn't log the user out.
        const status = (err as { response?: { status?: number } })?.response?.status;
        if (status === 401 || status === 403) clear();
      }
    })();
  }, [_hydrated]); // eslint-disable-line react-hooks/exhaustive-deps

  return <>{children}</>;
}
